import React, { useState, useEffect, useRef, useCallback } from "react";
import { useSelector } from "react-redux";
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";
import {
  Close,
  DragIndicator,
  PlayArrow,
  Stop,
} from "@mui/icons-material";
import sh from "bundles/common/utils/sh";
import TimerHandle from "bundles/Internal/components/common/timer/TimerHandle";
import RunningPanel from "bundles/Internal/components/common/timer/RunningPanel";
import PausedPanel from "bundles/Internal/components/common/timer/PausedPanel";
import IdlePanel from "bundles/Internal/components/common/timer/IdlePanel";
import CreateProjectDialog from "bundles/Internal/components/common/timer/CreateProjectDialog";
import CreateTaskDialog from "bundles/Internal/components/common/timer/CreateTaskDialog";

const STORAGE_KEY = "floating-timer-pos";
const TIMER_STATE_KEY = "floating-timer-state";
const TIME_ENTRY_CHANGED_EVENT = "time-entry:changed";
const TIME_PROJECT_CHANGED_EVENT = "time-project:changed";
const DEFAULT_PROJECT_COLOR = "#3b82f6";

function emitTimeEntryChanged(detail) {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(TIME_ENTRY_CHANGED_EVENT, { detail }));
  }
}

export function formatDuration(seconds) {
  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  return `${String(hrs).padStart(2, "0")}:${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
}

export const selectSx = {
  width: "100%",
  height: 40,
  fontSize: 14,
  fontFamily: "Segoe UI Regular, sans-serif, system-ui",
  backgroundColor: "hsl(140 10% 95%)",
  borderRadius: "12px",
  color: "hsl(150 30% 12%)",
  "& .MuiOutlinedInput-notchedOutline": { border: "1px solid hsl(140 8% 88%)" },
  "&:hover .MuiOutlinedInput-notchedOutline": { border: "1px solid hsl(140 8% 88%)" },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": { border: "1px solid hsl(150 38% 17%)", borderWidth: "1px" },
  "&.Mui-focused": { backgroundColor: "#ffffff" },
  transition: "border-color 0.2s ease, background-color 0.2s ease",
};

export const noteMultilineSx = {
  px: 1.5,
  py: 1,
  fontSize: 14,
  fontFamily: "Segoe UI Regular, sans-serif, system-ui",
  backgroundColor: "hsl(140 10% 95%)",
  borderRadius: "12px",
  border: "1px solid hsl(140 8% 88%)",
  color: "hsl(150 30% 12%)",
  alignItems: "flex-start",
  transition: "border-color 0.2s ease, background-color 0.2s ease",
  "&.Mui-focused": { borderColor: "hsl(150 38% 17%)", backgroundColor: "#ffffff" },
};

export const dialogInputSx = {
  width: "100%",
  height: 40,
  borderRadius: "12px",
  border: "1px solid hsl(140 8% 88%)",
  backgroundColor: "hsl(140 10% 95%)",
  px: 1.5,
  py: 1,
  fontSize: 14,
  fontFamily: "Segoe UI Regular, sans-serif, system-ui",
  color: "hsl(150 30% 12%)",
  outline: "none",
  boxSizing: "border-box",
  "&::placeholder": { color: "hsl(150 10% 46%)" },
  "&:focus": { outline: "2px solid hsl(150 38% 17%)", outlineOffset: "2px" },
};

export const dialogLabelSx = {
  fontSize: 12,
  fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
  color: "hsl(150 10% 46%)",
  mb: 0.75,
};

export const primaryButtonSx = {
  height: 40,
  borderRadius: "12px",
  textTransform: "none",
  fontSize: 14,
  fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
  backgroundColor: "hsl(150 38% 17%)",
  color: "hsl(80 97% 72%)",
  boxShadow: "none",
  "&:hover": { backgroundColor: "hsla(150, 38%, 17%, 0.9)", boxShadow: "none" },
  "&.Mui-disabled": {
    opacity: 0.5,
    backgroundColor: "hsl(150 38% 17%)",
    color: "hsl(80 97% 72%)",
    pointerEvents: "none",
  },
};

export const ghostButtonSx = {
  height: 40,
  borderRadius: "12px",
  textTransform: "none",
  fontSize: 14,
  fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
  color: "hsl(150 10% 46%)",
  "&:hover": { backgroundColor: "hsla(140, 8%, 90%, 0.5)" },
};

export const menuProps = {
  PaperProps: {
    elevation: 0,
    sx: {
      mt: 0.5,
      borderRadius: "12px",
      border: "1px solid hsl(140 8% 88%)",
      boxShadow: "0 8px 24px hsla(150, 30%, 12%, 0.10)",
      backgroundColor: "#ffffff",
      "& .MuiList-root": { py: 0.5 },
      "& .MuiMenuItem-root": {
        minHeight: 36,
        mx: 0.5,
        borderRadius: "8px",
        fontSize: 14,
        fontFamily: "Segoe UI Regular, sans-serif, system-ui",
        color: "hsl(150 30% 12%)",
        transition: "background-color 0.12s ease",
        "&:hover": { backgroundColor: "hsla(140, 8%, 90%, 0.6)" },
        "&.Mui-selected": { backgroundColor: "hsla(150, 38%, 17%, 0.08)" },
        "&.Mui-selected:hover": { backgroundColor: "hsla(150, 38%, 17%, 0.12)" },
        "&.Mui-disabled": { opacity: 1 },
      },
      "& .MuiDivider-root": { my: 0.5, borderColor: "hsl(140 8% 90%)" },
    },
  },
};

export default function FloatingTimer() {
  const authenticity_token = useSelector((s) => s.authenticity_token);

  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);
  const dragState = useRef(null);
  const ref = useRef(null);

  const [runningEntries, setRunningEntries] = useState([]);
  const [projects, setProjects] = useState([]);

  const [selectedProjectId, setSelectedProjectId] = useState("");
  const [selectedTaskId, setSelectedTaskId] = useState("");
  const [note, setNote] = useState("");

  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
  const [stoppedEntry, setStoppedEntry] = useState(null);

  const [projectDialogOpen, setProjectDialogOpen] = useState(false);
  const [taskDialogOpen, setTaskDialogOpen] = useState(false);
  const [taskDialogContext, setTaskDialogContext] = useState("idle");

  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) setPos(JSON.parse(raw));
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(TIMER_STATE_KEY);
      if (raw) {
        const state = JSON.parse(raw);
        if (state.note) setNote(state.note);
        if (state.selectedProjectId) setSelectedProjectId(state.selectedProjectId);
        if (state.selectedTaskId) setSelectedTaskId(state.selectedTaskId);
      }
    } catch { /* ignore */ }
  }, []);

  const fetchRunning = useCallback(() => {
    sh.get("/internal/time_entries/running?source=floating").then((res) => setRunningEntries(res.data));
  }, []);

  useEffect(() => { fetchRunning(); }, [fetchRunning]);

  useEffect(() => {
    const handler = (e) => {
      const detail = e?.detail;
      if (detail?.type === "start" && detail.entry) {
        setRunningEntries([detail.entry]);
        setNow(new Date(detail.entry.started_at).getTime());
        return;
      }
      if (detail?.type === "stop" && detail.entryId) {
        setRunningEntries((prev) => prev.filter((en) => en.id !== detail.entryId));
        return;
      }
      fetchRunning();
    };
    window.addEventListener(TIME_ENTRY_CHANGED_EVENT, handler);
    return () => window.removeEventListener(TIME_ENTRY_CHANGED_EVENT, handler);
  }, [fetchRunning]);

  useEffect(() => {
    if (runningEntries.length === 0) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [runningEntries.length]);

  const seededFromRunningRef = useRef(false);
  useEffect(() => {
    if (seededFromRunningRef.current) return;
    if (runningEntries.length === 0) return;
    const entry = runningEntries[0];
    if (entry.project?.id) setSelectedProjectId(entry.project.id);
    if (entry.task?.id) setSelectedTaskId(entry.task.id);
    if (entry.note) setNote(entry.note);
    seededFromRunningRef.current = true;
  }, [runningEntries]);

  useEffect(() => {
    if (!open) return;
    sh.get("/internal/time_projects?caller=floating").then((res) => setProjects(res.data));
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handler = () => {
      sh.get("/internal/time_projects?caller=floating").then((res) => setProjects(res.data));
    };
    window.addEventListener(TIME_PROJECT_CHANGED_EVENT, handler);
    return () => window.removeEventListener(TIME_PROJECT_CHANGED_EVENT, handler);
  }, [open]);

  useEffect(() => {
    try {
      localStorage.setItem(TIMER_STATE_KEY, JSON.stringify({
        note,
        selectedProjectId,
        selectedTaskId
      }));
    } catch { /* ignore */ }
  }, [note, selectedProjectId, selectedTaskId]);

  const isRunning = runningEntries.length > 0;

  // Persist note edits to the backend while running so a reload doesn't lose them.
  const noteSaveRef = useRef(null);
  const lastSavedNoteRef = useRef(null);
  useEffect(() => {
    if (!isRunning) return undefined;
    const entry = runningEntries[0];
    if (!entry) return undefined;
    if (note === (entry.note || "") || note === lastSavedNoteRef.current) return undefined;

    clearTimeout(noteSaveRef.current);
    noteSaveRef.current = setTimeout(() => {
      sh.put(`/internal/time_entries/${entry.id}`, {
        authenticity_token,
        time_entry: { note },
      }).then(() => {
        lastSavedNoteRef.current = note;
        setRunningEntries((prev) => prev.map((e) => (e.id === entry.id ? { ...e, note } : e)));
      }).catch(() => {});
    }, 600);

    return () => clearTimeout(noteSaveRef.current);
  }, [note, isRunning, runningEntries, authenticity_token]);

  const elapsedTime = (() => {
    if (!isRunning) return 0;
    const oldest = runningEntries.reduce((a, b) =>
      new Date(a.started_at) < new Date(b.started_at) ? a : b,
    );
    return Math.max(0, Math.floor((now - new Date(oldest.started_at).getTime()) / 1000));
  })();

  const coloredProjects = projects.map((p) => ({
    ...p,
    color: p.color || DEFAULT_PROJECT_COLOR,
  }));

  const selectedProject = coloredProjects.find((p) => p.id === selectedProjectId) || null;

  const startTimer = () => {
    if (!selectedProjectId || !selectedTaskId) return;
    const payload = {
      authenticity_token,
      time_entry: {
        time_project_id: selectedProjectId,
        time_task_id: selectedTaskId,
        ...(note && { note }),
      },
    };
    setNow(Date.now());
    sh.post("/internal/time_entries", payload).then((res) => {
      setNow(new Date(res.data.started_at).getTime());
      setRunningEntries((prev) => [...prev, res.data]);
      emitTimeEntryChanged({ type: "start", entry: res.data });
    });
  };

  const stopTimer = () => {
    const entry = runningEntries[0];
    if (!entry) return;
    const frozenElapsed = elapsedTime;
    sh.patch(`/internal/time_entries/${entry.id}/stop`, {
      authenticity_token,
      time_entry: { note: note || "" },
    }).then((res) => {
      setRunningEntries((prev) => prev.filter((e) => e.id !== res.data.id));
      setStoppedEntry({
        id:        res.data.id,
        projectId: res.data.project?.id || "",
        taskId:    res.data.task?.id || "",
        note:      res.data.note || "",
        frozenElapsed,
      });
      emitTimeEntryChanged({ type: "stop", entryId: res.data.id });
    });
  };

  const resumeTimer = () => {
    if (!stoppedEntry) return;
    sh.patch(`/internal/time_entries/${stoppedEntry.id}/resume`, {
      authenticity_token,
      time_entry: {
        ...(stoppedEntry.projectId && { time_project_id: stoppedEntry.projectId }),
        time_task_id: stoppedEntry.taskId || null,
        note: stoppedEntry.note || "",
      },
    }).then((res) => {
      setSelectedProjectId(res.data.project?.id || "");
      setSelectedTaskId(res.data.task?.id || "");
      setNote(res.data.note || "");
      setRunningEntries([res.data]);
      setNow(new Date(res.data.started_at).getTime() + stoppedEntry.frozenElapsed * 1000);
      setStoppedEntry(null);
      emitTimeEntryChanged({ type: "start", entry: res.data });
    });
  };

  const confirmStoppedEntry = () => {
    if (!stoppedEntry) return;
    sh.put(`/internal/time_entries/${stoppedEntry.id}`, {
      authenticity_token,
      time_entry: {
        ...(stoppedEntry.projectId && { time_project_id: stoppedEntry.projectId }),
        time_task_id: stoppedEntry.taskId || null,
        note: stoppedEntry.note || "",
      },
    }).then(() => {
      setStoppedEntry(null);
      setNote("");
      emitTimeEntryChanged();
    });
  };

  const openCancelConfirm = () => setCancelConfirmOpen(true);
  const closeCancelConfirm = () => setCancelConfirmOpen(false);

  const confirmCancelTimer = () => {
    const entry = runningEntries[0];
    const targetId = entry?.id || stoppedEntry?.id;
    if (!targetId) {
      closeCancelConfirm();
      return;
    }
    sh.delete(`/internal/time_entries/${targetId}`, {
      data: { authenticity_token },
    }).then(() => {
      setRunningEntries((prev) => prev.filter((e) => e.id !== targetId));
      setStoppedEntry(null);
      setNote("");
      closeCancelConfirm();
      emitTimeEntryChanged();
    });
  };

  const handleToggle = () => {
    if (isRunning) return stopTimer();
    if (stoppedEntry) return resumeTimer();
    return startTimer();
  };

  const handleProjectCreated = (project) => {
    setProjects((prev) => [...prev, project]);
    setSelectedProjectId(project.id);
    setSelectedTaskId("");
    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent(TIME_PROJECT_CHANGED_EVENT));
    }
  };

  const openTaskDialog = (context) => {
    setTaskDialogContext(context);
    setTaskDialogOpen(true);
  };

  const handleTaskCreated = (task, projectId) => {
    setProjects((prev) =>
      prev.map((p) =>
        p.id === projectId
          ? { ...p, tasks: [...(p.tasks || []), task] }
          : p,
      ),
    );
    if (taskDialogContext === "manual") {
      setSelectedProjectId(projectId);
      setSelectedTaskId(task.id);
    } else {
      setSelectedTaskId(task.id);
    }
    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent(TIME_PROJECT_CHANGED_EVENT));
    }
  };

  const onPointerDown = (e) => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    dragState.current = { dx: e.clientX - rect.left, dy: e.clientY - rect.top };
    e.target.setPointerCapture(e.pointerId);
  };

  const onPointerMove = (e) => {
    if (!dragState.current) return;
    const w = ref.current?.offsetWidth ?? 0;
    const h = ref.current?.offsetHeight ?? 0;
    const x = Math.min(window.innerWidth - w - 8, Math.max(8, e.clientX - dragState.current.dx));
    const y = Math.min(window.innerHeight - h - 8, Math.max(8, e.clientY - dragState.current.dy));
    setPos({ x, y });
  };

  const onPointerUp = (e) => {
    if (pos) localStorage.setItem(STORAGE_KEY, JSON.stringify(pos));
    dragState.current = null;
    e.target.releasePointerCapture?.(e.pointerId);
  };

  const cardStyle = pos
    ? { left: pos.x, top: pos.y, right: "auto", transform: "none" }
    : { right: 24, top: "50%", transform: "translate3d(0, -50%, 0)" };

  if (!open) {
    return (
      <TimerHandle
        onOpen={() => setOpen(true)}
        isRunning={isRunning}
        elapsedTime={elapsedTime}
      />
    );
  }

  return (
    <>
      <Paper
        ref={ref}
        elevation={0}
        sx={{
          position: "fixed",
          zIndex: 1300,
          width: 320,
          borderRadius: "16px",
          border: "1px solid hsl(30, 8%, 90%)",
          overflow: "hidden",
          boxShadow: "0 8px 32px hsla(152, 38%, 14%, 0.18)",
          fontFamily: "Segoe UI Semibold, system-ui, sans-serif",
          "@keyframes slide-in-right": {
            from: { transform: "translate3d(110%, -50%, 0)", opacity: 0 },
            to: { transform: "translate3d(0, -50%, 0)", opacity: 1 },
          },
          animation: pos ? "none" : "slide-in-right 0.28s cubic-bezier(0.22, 1, 0.36, 1)",
          ...cardStyle,
        }}
      >
        <Box
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 1.5,
            py: 1,
            bgcolor: "hsl(150 38% 17%)",
            cursor: "grab",
            userSelect: "none",
            "&:active": { cursor: "grabbing" },
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <DragIndicator sx={{ color: "rgba(255,255,255,0.7)", fontSize: 16 }} />
            <Typography
              sx={{
                color: "white",
                fontSize: 11,
                fontFamily: "Segoe UI Semibold, system-ui, sans-serif",
                letterSpacing: 1.2,
                textTransform: "uppercase",
              }}
            >
              Tempo Timer
            </Typography>
          </Box>

          <IconButton
            size="small"
            aria-label="Close timer"
            onPointerDown={(e) => e.stopPropagation()}
            onClick={() => setOpen(false)}
            sx={{
              color: "white",
              width: 24,
              height: 24,
              borderRadius: "6px",
              "&:hover": { bgcolor: "rgba(255,255,255,0.1)" },
            }}
          >
            <Close sx={{ fontSize: 14 }} />
          </IconButton>
        </Box>

        {/* Timer row */}
        <Box sx={{ px: 2.5, py: 2.5, display: "flex", alignItems: "center", gap: 2 }}>
          <IconButton
            onClick={handleToggle}
            disabled={!isRunning && !stoppedEntry && (!selectedProjectId || !selectedTaskId)}
            aria-label={isRunning ? "Stop timer" : stoppedEntry ? "Resume timer" : "Start timer"}
            sx={{
              width: 48,
              height: 48,
              borderRadius: "50%",
              flexShrink: 0,
              bgcolor: isRunning ? "hsl(0, 78%, 60%)" : "hsl(80, 75%, 65%)",
              color: isRunning ? "white" : "hsl(152, 38%, 14%)",
              transition: "transform 0.15s ease, background-color 0.2s ease",
              "&:hover": {
                bgcolor: isRunning ? "hsl(0, 78%, 55%)" : "hsla(80, 75%, 65%, 0.9)",
              },
              "&.Mui-disabled": { bgcolor: "hsl(0, 0%, 88%)", color: "hsl(0, 0%, 60%)" },
            }}
          >
            {isRunning
              ? <Stop sx={{ fontSize: 16 }} />
              : <PlayArrow />}
          </IconButton>

          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                fontSize: 10,
                textTransform: "uppercase",
                letterSpacing: 1.2,
                color: "hsl(0, 0%, 40%)",
                fontFamily: "Segoe UI Semibold, system-ui, sans-serif",
                mb: 0.25,
              }}
            >
              {isRunning ? "Running" : stoppedEntry ? "Paused" : "Idle"}
            </Typography>

            <Typography
              sx={{
                fontFamily: "Segoe UI Semibold, system-ui, sans-serif",
                fontSize: 24,
                lineHeight: 1,
                letterSpacing: -0.5,
                color: "hsl(0, 0%, 8%)",
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {formatDuration(stoppedEntry?.frozenElapsed ?? elapsedTime)}
            </Typography>
          </Box>
        </Box>

        {/* Running state — note editable live + cancel button. Stop is the big button in the timer row. */}
        <RunningPanel
          open={isRunning && !stoppedEntry}
          selectedProject={selectedProject}
          note={note}
          setNote={setNote}
          onCancel={openCancelConfirm}
        />

        {/* Paused/review state — entry is stopped on backend, user can edit + confirm or resume */}
        <PausedPanel
          open={!!stoppedEntry}
          stoppedEntry={stoppedEntry}
          setStoppedEntry={setStoppedEntry}
          coloredProjects={coloredProjects}
          onCancel={openCancelConfirm}
          onConfirm={confirmStoppedEntry}
        />

        {/* Idle form — shown before starting a timer */}
        <IdlePanel
          open={!isRunning && !stoppedEntry}
          selectedProjectId={selectedProjectId}
          setSelectedProjectId={setSelectedProjectId}
          selectedTaskId={selectedTaskId}
          setSelectedTaskId={setSelectedTaskId}
          selectedProject={selectedProject}
          coloredProjects={coloredProjects}
          note={note}
          setNote={setNote}
          onCreateProject={() => setProjectDialogOpen(true)}
          onCreateTask={() => openTaskDialog("manual")}
        />
      </Paper>

      <CreateProjectDialog
        open={projectDialogOpen}
        onClose={() => setProjectDialogOpen(false)}
        authenticity_token={authenticity_token}
        onCreated={handleProjectCreated}
      />

      <CreateTaskDialog
        open={taskDialogOpen}
        onClose={() => setTaskDialogOpen(false)}
        context={taskDialogContext}
        selectedProjectId={selectedProjectId}
        coloredProjects={coloredProjects}
        authenticity_token={authenticity_token}
        onCreated={handleTaskCreated}
      />

      {/* Cancel-timer confirmation */}
      <Dialog
        open={cancelConfirmOpen}
        onClose={closeCancelConfirm}
        PaperProps={{ sx: { borderRadius: "16px", minWidth: 360, maxWidth: 400 } }}
      >
        <DialogTitle
          sx={{
            fontSize: 16,
            fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
            color: "hsl(150 30% 12%)",
            pb: 0.5,
            pt: 2.5,
            px: 3,
          }}
        >
          Discard this time entry?
        </DialogTitle>
        <DialogContent sx={{ pt: 1.5, px: 3, pb: 1 }}>
          <Typography sx={{ fontSize: 13, fontFamily: "Segoe UI Regular, sans-serif, system-ui", color: "hsl(150 10% 46%)", lineHeight: 1.5 }}>
            The timer will stop and this entry will be deleted. The elapsed time will not be saved.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, pt: 1, gap: 1 }}>
          <Button onClick={closeCancelConfirm} sx={ghostButtonSx}>
            Keep running
          </Button>
          <Button
            onClick={confirmCancelTimer}
            variant="contained"
            disableElevation
            sx={{
              ...primaryButtonSx,
              backgroundColor: "hsl(0, 70%, 45%)",
              color: "#ffffff",
              "&:hover": { backgroundColor: "hsl(0, 70%, 40%)", boxShadow: "none" },
              "&.Mui-disabled": { ...primaryButtonSx["&.Mui-disabled"], backgroundColor: "hsl(0, 70%, 45%)", color: "#ffffff" },
            }}
          >
            Discard entry
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
